/**
 * Which world is being looked at, and what is being edited about it.
 *
 * Lifted out of session-management, a file that holds four components. The top
 * one carried eight pieces of state that interlock around a single selection:
 * picking a world loads its sessions and closes any session form that was open
 * for the previous one, and deleting the selected world has to clear the
 * selection as well as the list.
 *
 * The three form components below it keep their own state -- a form's fields
 * belong to the form.
 */
import { loadFailure, deleteFailure, requestFailure, type FormFailure } from './save-failure-words';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminCohorts } from '../../api/admin/cohorts/hooks';
import type { CohortItem } from '../../api/admin/cohorts/types';
import {
  useAdminSessions,
  useAdminWorlds,
  useDeleteSession,
  useDeleteWorld,
  useInterruptedSessionIds,
  useResetWorldSave,
} from '../../api/admin/sessions/hooks';
import type { SessionItem, WorldItem } from '../../api/admin/sessions/types';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import { showToast } from '../../components/toast/toast-store';

export type WorldStatusFilter = '' | 'active' | 'inactive';

export function useWorldSessionBrowser() {
  const { t } = useTranslation();
  const worldsQuery = useAdminWorlds(1, 100);
  const cohortsQuery = useAdminCohorts();
  const deleteWorld = useDeleteWorld();
  const deleteSession = useDeleteSession();
  const resetWorldSave = useResetWorldSave();
  // The world whose save is being reset: its button waits, and no second reset starts.
  const resettingWorldId = resetWorldSave.isPending ? resetWorldSave.variables?.id ?? null : null;
  const worlds: WorldItem[] = worldsQuery.data ?? [];
  const cohorts: CohortItem[] = (cohortsQuery.data ?? []).filter((c) => c.isActive);
  const isLoadingWorlds = worldsQuery.isLoading;
  const [selectedWorld, setSelectedWorld] = useState<WorldItem | null>(null);
  const sessionsQuery = useAdminSessions(selectedWorld?.gameName ?? null, selectedWorld?.name ?? null);
  const sessions: SessionItem[] = sessionsQuery.data ?? [];
  const isLoadingSessions = sessionsQuery.isLoading && !!selectedWorld;
  // The running sessions whose world the game server runs no match on, which
  // their cards say are interrupted (useInterruptedSessionIds).
  const interruptedSessionIds = useInterruptedSessionIds(
    selectedWorld?.gameName ?? null,
    selectedWorld?.name ?? null,
    sessions.some((s) => s.status === 'RUNNING'),
  );
  const [pageError, setPageError] = useState<FormFailure | null>(null);
  const queryError = worldsQuery.isError
    ? loadFailure(t, worldsQuery.error, 'worlds')
    : sessionsQuery.isError
      ? loadFailure(t, sessionsQuery.error, 'sessions')
      : cohortsQuery.isError ? loadFailure(t, cohortsQuery.error, 'cohorts') : null;
  const error = pageError ?? queryError;
  const [showCreateWorldForm, setShowCreateWorldForm] = useState(false);
  const [editingWorld, setEditingWorld] = useState<WorldItem | null>(null);
  const [showCreateSessionForm, setShowCreateSessionForm] = useState(false);
  const [editingSession, setEditingSession] = useState<SessionItem | null>(null);
  const [worldSearch, setWorldSearch] = useState('');
  const [worldStatusFilter, setWorldStatusFilter] = useState<WorldStatusFilter>('');

  const handleSelectWorld = (world: WorldItem) => {
    setSelectedWorld(world);
    setShowCreateSessionForm(false);
    setEditingSession(null);
  };

  const handleDeleteWorld = async (world: WorldItem) => {
    if (!(await confirmDialog({ message: t('admin.worlds.confirmDelete', { name: world.name }), tone: 'danger' }))) return;
    setPageError(null);
    try {
      await deleteWorld.mutateAsync(world.id);
      if (selectedWorld?.id === world.id) {
        setSelectedWorld(null);
      }
    } catch (e) {
      setPageError(deleteFailure(t, e, 'world'));
    }
  };

  // Erases the world's saved progress for every team and ends its running
  // matches: asked first, in words that say both. Matches the game server
  // could not tell to end run on, unable to save, and the admin is told so.
  const handleResetWorldSave = async (world: WorldItem) => {
    if (resetWorldSave.isPending) return;
    const confirmed = await confirmDialog({
      title: t('admin.worlds.resetSave.confirmTitle', { name: world.name }),
      message: t('admin.worlds.resetSave.confirmMessage', { name: world.name }),
      confirmLabel: t('admin.worlds.resetSave.confirmLabel'),
      tone: 'danger',
    });
    if (!confirmed) return;
    setPageError(null);
    try {
      const { matches } = await resetWorldSave.mutateAsync(world);
      if (matches.failed > 0) {
        showToast('info', t('admin.worlds.resetSave.done', { name: world.name }), t('admin.worlds.resetSave.matchesNotTold', { count: matches.failed }));
      } else {
        showToast('success', t('admin.worlds.resetSave.done', { name: world.name }));
      }
    } catch (e) {
      setPageError(requestFailure(t, e, 'resetWorldSave'));
    }
  };

  const handleWorldSaved = () => {
    setShowCreateWorldForm(false);
    setEditingWorld(null);
  };

  const handleDeleteSession = async (session: SessionItem) => {
    if (!(await confirmDialog({ message: t('admin.sessions.confirmDelete'), tone: 'danger' }))) return;
    setPageError(null);
    try {
      await deleteSession.mutateAsync({
        gameName: session.gameName,
        worldName: session.worldName,
        externalId: session.externalId,
      });
    } catch (e) {
      setPageError(deleteFailure(t, e, 'session'));
    }
  };

  const handleSessionSaved = () => {
    setShowCreateSessionForm(false);
    setEditingSession(null);
  };

  const wq = worldSearch.toLowerCase();
  const filteredWorlds = worlds.filter((w) => {
    const matchesSearch = !wq ||
      w.name.toLowerCase().includes(wq) ||
      w.gameName.toLowerCase().includes(wq);
    const matchesStatus =
      worldStatusFilter === '' ||
      (worldStatusFilter === 'active' && w.isActive) ||
      (worldStatusFilter === 'inactive' && !w.isActive);
    return matchesSearch && matchesStatus;
  });

  return {
    worlds,
    filteredWorlds,
    cohorts,
    isLoadingWorlds,
    selectedWorld,
    sessions,
    isLoadingSessions,
    interruptedSessionIds,
    error,
    showCreateWorldForm,
    setShowCreateWorldForm,
    editingWorld,
    setEditingWorld,
    showCreateSessionForm,
    setShowCreateSessionForm,
    editingSession,
    setEditingSession,
    worldSearch,
    setWorldSearch,
    worldStatusFilter,
    setWorldStatusFilter,
    handleSelectWorld,
    handleDeleteWorld,
    handleResetWorldSave,
    resettingWorldId,
    handleWorldSaved,
    handleDeleteSession,
    handleSessionSaved,
  };
}
